import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Copy, Star } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { REVIEW_URL, SERVICES } from "../lib/i18n";
import { reviewText as template, toIntl } from "../lib/review";

export default function ReviewRequest() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [msg, setMsg] = useState(template("", ""));
  const [edited, setEdited] = useState(false);
  const services = SERVICES.flatMap((s) => s.it.s);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Richiedi recensione · Gioia";
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!edited) setMsg(template(name.trim().split(" ")[0], service));
  }, [name, service, edited]);

  const num = toIntl(phone);
  const waHref = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(msg); toast.success("Messaggio copiato"); } catch { toast.error("Copia non riuscita"); }
  };
  const field = "w-full bg-transparent border-0 border-b border-[#ddd4c7] py-3 text-[#14110F] outline-none focus:border-[#8A6A3A] transition-colors duration-300";

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto grid lg:grid-cols-12 gap-12" data-testid="review-request-page">
        <div className="lg:col-span-5">
          <p className="overline text-[#8A6A3A]">Area Gioia · uso interno</p>
          <h1 className="mt-5 font-serif-d font-light text-5xl sm:text-6xl leading-[0.95] text-[#14110F]">Richiedi una <em className="text-[#8A6A3A]">recensione</em></h1>
          <p className="mt-6 text-[#4f4841] font-light leading-relaxed">Dopo il trattamento inserisci nome e numero della cliente: con un tocco si apre WhatsApp con il messaggio già pronto e il link alle recensioni Google. Nessun dato viene salvato.</p>
          <a href={REVIEW_URL} target="_blank" rel="noreferrer" data-testid="review-link-preview" className="mt-8 inline-flex items-center gap-2 text-sm text-[#8A6A3A] underline underline-offset-4">
            <Star className="w-4 h-4" /> Apri la pagina recensioni Google
          </a>
        </div>
        <div className="lg:col-span-7 space-y-8">
          <div className="grid sm:grid-cols-2 gap-8">
            <label className="block"><span className="overline text-[0.62rem] text-[#5a534c]">Nome cliente</span>
              <input data-testid="review-name-input" className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Maria" />
            </label>
            <label className="block"><span className="overline text-[0.62rem] text-[#5a534c]">Cellulare WhatsApp</span>
              <input data-testid="review-phone-input" type="tel" className={field} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Es. 333 123 4567" />
            </label>
          </div>
          <label className="block"><span className="overline text-[0.62rem] text-[#5a534c]">Trattamento (facoltativo)</span>
            <select data-testid="review-service-select" className={`${field} cursor-pointer`} value={service} onChange={(e) => setService(e.target.value)}>
              <option value="">—</option>
              {services.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block"><span className="overline text-[0.62rem] text-[#5a534c]">Messaggio</span>
            <textarea data-testid="review-message-input" rows={6} className={`${field} resize-none leading-relaxed`} value={msg} onChange={(e) => { setEdited(true); setMsg(e.target.value); }} />
          </label>
          {edited && (
            <button type="button" data-testid="review-reset-btn" onClick={() => setEdited(false)} className="text-xs text-[#8A6A3A] underline underline-offset-4">Ripristina messaggio standard</button>
          )}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              data-testid="review-whatsapp-btn"
              href={num.length >= 11 ? waHref : undefined}
              target="_blank"
              rel="noreferrer"
              aria-disabled={num.length < 11}
              onClick={(e) => { if (num.length < 11) { e.preventDefault(); toast.error("Inserisci un numero di cellulare valido"); } }}
              className={`inline-flex justify-center items-center gap-3 rounded-full px-8 py-4 text-[0.75rem] tracking-[0.2em] uppercase transition-colors duration-500 ${num.length >= 11 ? "bg-[#14110F] text-[#F6F2EC] hover:bg-[#8A6A3A]" : "bg-[#14110F]/40 text-[#F6F2EC] cursor-not-allowed"}`}
            >
              <MessageCircle className="w-4 h-4" /> Invia su WhatsApp
            </a>
            <button type="button" data-testid="review-copy-btn" onClick={copy} className="inline-flex justify-center items-center gap-3 rounded-full border border-[#14110F] px-8 py-4 text-[0.75rem] tracking-[0.2em] uppercase text-[#14110F] transition-colors duration-500 hover:bg-[#14110F] hover:text-[#F6F2EC]">
              <Copy className="w-4 h-4" /> Copia messaggio
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
